import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Building2, MapPin, Globe, Clock, DollarSign, CheckCircle, Shield, Zap } from "lucide-react";
import api from "../lib/api";

export default function BusinessProfilePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/api/businesses/${slug}`)
      .then(r => setBusiness(r.data))
      .catch(() => setError("Business not found"))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div style={{ paddingTop: 100, textAlign: "center" }}><div style={{ width: 40, height: 40, border: "3px solid rgba(92,174,255,0.2)", borderTop: "3px solid #5CAEFF", borderRadius: "50%", margin: "0 auto", animation: "spin 0.8s linear infinite" }} /><style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style></div>;
  if (error || !business) return (
    <div style={{ paddingTop: 100, textAlign: "center", color: "#888", fontFamily: "Satoshi, sans-serif" }}>
      <p>{error || "Not found"}</p>
      <button className="btn-secondary" onClick={() => navigate("/")} style={{ marginTop: 16 }}>Go home</button>
    </div>
  );

  return (
    <div style={{ paddingTop: 80, minHeight: "100vh", background: "var(--bg-base)" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "2rem 24px 64px" }}>
        <button onClick={() => navigate(-1)} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: "1.5rem", background: "none", border: "none", cursor: "pointer", color: "#888", fontSize: "0.85rem", fontFamily: "Satoshi, sans-serif" }}>
          <ArrowLeft size={15} /> Back
        </button>

        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
          {/* Business Header */}
          <div className="glass-card" data-testid="business-profile-header" style={{ padding: "2rem", marginBottom: "2rem" }}>
            <div style={{ display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
              <div style={{ width: 64, height: 64, borderRadius: 18, background: "linear-gradient(135deg, rgba(92,174,255,0.2), rgba(111,255,207,0.2))", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Building2 size={28} color="#5CAEFF" />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: "0.4rem", flexWrap: "wrap" }}>
                  <h1 style={{ fontFamily: "Outfit, sans-serif", fontWeight: 800, fontSize: "1.6rem", letterSpacing: "-0.02em", color: "#1A1A1A" }}>{business.business_name}</h1>
                  <span style={{ fontSize: "0.72rem", fontWeight: 600, background: "rgba(92,174,255,0.1)", color: "#2980D8", border: "1px solid rgba(92,174,255,0.2)", padding: "0.15rem 0.55rem", borderRadius: 100, fontFamily: "Outfit, sans-serif" }}>{business.business_type}</span>
                </div>
                <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: "0.8rem" }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: "0.82rem", color: "#888", fontFamily: "Satoshi, sans-serif" }}>
                    <MapPin size={12} /> {business.city}, {business.country}
                  </span>
                  {business.website && (
                    <a href={business.website} target="_blank" rel="noreferrer" style={{ display: "flex", alignItems: "center", gap: 4, fontSize: "0.82rem", color: "#5CAEFF", textDecoration: "none", fontFamily: "Satoshi, sans-serif" }}>
                      <Globe size={12} /> {business.website?.replace("https://", "")}
                    </a>
                  )}
                </div>
                <p style={{ color: "#555", fontSize: "0.88rem", lineHeight: 1.6, fontFamily: "Satoshi, sans-serif", maxWidth: 600 }}>{business.description}</p>
              </div>
              <div style={{ display: "flex", gap: 12 }}>
                <div style={{ textAlign: "center", padding: "0.7rem 1.2rem", background: "rgba(92,174,255,0.08)", borderRadius: 12, border: "1px solid rgba(92,174,255,0.15)" }}>
                  <div style={{ fontFamily: "Outfit, sans-serif", fontWeight: 800, fontSize: "1.5rem", color: "#5CAEFF" }}>{business.trust_score}</div>
                  <div style={{ fontSize: "0.7rem", color: "#AAA", fontFamily: "Satoshi, sans-serif" }}>Trust</div>
                </div>
                <div style={{ textAlign: "center", padding: "0.7rem 1.2rem", background: "rgba(111,255,207,0.08)", borderRadius: 12, border: "1px solid rgba(111,255,207,0.2)" }}>
                  <div style={{ fontFamily: "Outfit, sans-serif", fontWeight: 800, fontSize: "1.5rem", color: "#00A86B" }}>{business.record_count || 0}</div>
                  <div style={{ fontSize: "0.7rem", color: "#AAA", fontFamily: "Satoshi, sans-serif" }}>Executions</div>
                </div>
              </div>
            </div>

            {/* Categories & Industries */}
            {(business.task_categories?.length > 0 || business.industries_served?.length > 0) && (
              <div style={{ marginTop: "1.2rem", paddingTop: "1.2rem", borderTop: "1px solid rgba(0,0,0,0.05)", display: "flex", gap: 8, flexWrap: "wrap" }}>
                {business.task_categories?.map(cat => (
                  <span key={cat} style={{ fontSize: "0.72rem", fontWeight: 600, background: "rgba(92,174,255,0.08)", color: "#2980D8", border: "1px solid rgba(92,174,255,0.15)", padding: "0.2rem 0.6rem", borderRadius: 100, fontFamily: "Outfit, sans-serif" }}>{cat.replace(/_/g, " ")}</span>
                ))}
                {business.industries_served?.map(ind => (
                  <span key={ind} style={{ fontSize: "0.72rem", fontWeight: 600, background: "rgba(111,255,207,0.08)", color: "#00A86B", border: "1px solid rgba(111,255,207,0.2)", padding: "0.2rem 0.6rem", borderRadius: 100, fontFamily: "Outfit, sans-serif" }}>{ind.replace(/_/g, " ")}</span>
                ))}
              </div>
            )}
          </div>

          {/* Execution Records */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: "1.2rem" }}>
            <div className="trust-bar" />
            <h2 style={{ fontFamily: "Outfit, sans-serif", fontWeight: 700, fontSize: "1.1rem", color: "#1A1A1A" }}>Execution records</h2>
          </div>

          {(!business.recent_records || business.recent_records.length === 0) ? (
            <div className="glass-card" style={{ padding: "3rem", textAlign: "center" }}>
              <Zap size={32} color="#DDD" style={{ margin: "0 auto 1rem" }} />
              <p style={{ color: "#888", fontFamily: "Satoshi, sans-serif" }}>No execution records yet.</p>
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 16 }}>
              {business.recent_records.map((rec, i) => (
                <motion.div key={rec.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}>
                  <div data-testid={`biz-record-${i}`} className="glass-card" style={{ padding: "1.4rem" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.7rem" }}>
                      <div style={{ fontFamily: "Outfit, sans-serif", fontWeight: 700, fontSize: "0.9rem", color: "#1A1A1A", flex: 1, paddingRight: 8 }}>{rec.title}</div>
                      {rec.verification_status === "verified" && <span className="badge-verified">Verified</span>}
                    </div>
                    <p style={{ fontSize: "0.78rem", color: "#666", lineHeight: 1.5, fontFamily: "Satoshi, sans-serif", marginBottom: "0.8rem" }}>{rec.narrative_summary?.slice(0, 130)}...</p>
                    {rec.result_metrics && Object.keys(rec.result_metrics).length > 0 && (
                      <div style={{ background: "rgba(92,174,255,0.05)", borderRadius: 8, padding: "0.6rem 0.8rem", marginBottom: "0.8rem" }}>
                        {Object.entries(rec.result_metrics).slice(0, 2).map(([k, v]) => (
                          <div key={k} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <span style={{ fontSize: "0.7rem", color: "#AAA", fontFamily: "Satoshi, sans-serif" }}>{k.replace(/_/g, " ")}</span>
                            <span style={{ fontSize: "0.78rem", fontWeight: 700, color: "#5CAEFF", fontFamily: "Outfit, sans-serif" }}>{String(v)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    <div style={{ display: "flex", gap: 12 }}>
                      <span style={{ fontSize: "0.72rem", color: "#AAA", display: "flex", alignItems: "center", gap: 3 }}><Clock size={10} /> {rec.time_taken_days}d</span>
                      <span style={{ fontSize: "0.72rem", color: "#AAA", display: "flex", alignItems: "center", gap: 3 }}><DollarSign size={10} /> ${rec.budget_min?.toLocaleString()}–${rec.budget_max?.toLocaleString()}</span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}
